import { environment } from '@environments/environment';
import { Injectable, OnDestroy } from '@angular/core';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { Subscription, timer } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { AuthService } from './auth/auth.service';

type WebSocketMessage = {
  userId: string;
  eventType: string;
  domain: string;
  payload?: any;
};

type WebSocketHandler = (message: WebSocketMessage) => void;

@Injectable({
  providedIn: 'root'
})
export class WebSocketService implements OnDestroy {
  private socket: WebSocketSubject<WebSocketMessage> | null = null;
  private handlers: { [key: string]: WebSocketHandler } = {};
  private subscription: Subscription | null = null;
  private heartbeatSubscription: Subscription | null = null;
  private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private isDestroyed = false;

  constructor(
    private cookieService: CookieService,
    private authService: AuthService
  ) {
    this.initializeWebSocket();
  }

  private async initializeWebSocket(): Promise<void> {
    if (this.isDestroyed) {
      return;
    }

    let jwt: string | undefined;

    // Try Cognito token first (SSO users)
    if (this.authService.isAuthenticated()) {
      const session = await this.authService.getAuthSession();
      jwt = session?.tokens?.idToken?.toString();
    }

    // Fallback to cookie-based token (IDaaS users)
    if (!jwt) {
      const jwtCookieName = environment.jwtCookieName;
      jwt = this.cookieService.get(jwtCookieName);
    }

    if (!jwt) {
      console.warn('WebSocket: No authentication token available');
      return;
    }

    this.socket = webSocket({
      url: environment.webSocketUrl,
      protocol: [jwt]
    });
    
    this.subscription = this.socket.subscribe(
      (message: WebSocketMessage) => this.handleMessage(message),
      (err) => {
        console.error('WebSocket error:', err);
        this.handleReconnection();
      },
      () => {
        console.warn('WebSocket connection closed');
        this.handleReconnection();
      }
    );

    this.reconnectAttempts = 0;
    this.startHeartbeat();
  }

  private startHeartbeat(): void {
    this.heartbeatSubscription?.unsubscribe();
    
    // Send ping every 30 seconds to keep connection alive
    this.heartbeatSubscription = timer(30000, 30000).subscribe(() => {
      if (this.socket && !this.isDestroyed) {
        try {
          this.socket.next({
            userId: '',
            eventType: 'ping',
            domain: 'keepalive'
          });
        } catch (error) {
          console.error('Failed to send heartbeat:', error);
        }
      }
    });
  }

  private handleReconnection(): void {
    this.cleanup();

    if (this.isDestroyed || this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('WebSocket: Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    
    console.log(`WebSocket: Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    
    this.reconnectTimeoutId = setTimeout(() => {
      this.initializeWebSocket();
    }, delay);
  }

  private cleanup(): void {
    if (this.reconnectTimeoutId !== null) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
    this.heartbeatSubscription?.unsubscribe();
    this.heartbeatSubscription = null;
    this.subscription?.unsubscribe();
    this.subscription = null;
    this.socket?.complete();
    this.socket = null;
  }

  registerHandler(eventType: string, handler: WebSocketHandler): void {
    this.handlers[eventType] = handler;
  }

  private handleMessage(message: WebSocketMessage): void {
    const handler = this.handlers[message.eventType];
    if (handler) {
      handler(message);
    } else {
      console.warn(`No handler registered for message type: ${message.eventType}`);
    }
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.cleanup();
  }
}
